/**
 * Hooks TanStack Query per le pagine Rosa/Tesoreria/Rinnovi (FASE 2 iter 6).
 *
 * Read-only: queste pagine mostrano lo stato corrente, le mutazioni
 * arrivano in FASE 3+ (mercato).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuthStore } from "../lib/auth-store";

export interface MovimentoBudget {
  id: string;
  importo_milioni: string;
  tipo: string;
  descrizione: string;
  data_movimento: string;
  storna_movimento_id: string | null;
}

export interface BudgetSaldo {
  squadra_id: string;
  stagione_id: string;
  saldo_milioni: string | null;
  num_movimenti: number;
  ultimi_movimenti: MovimentoBudget[];
  // FASE 7 iter 2 — privacy
  privacy_visibility?: "visibile" | "aggregato" | "nascosto";
}

export interface MovimentoTesoreria {
  id: string;
  importo_euro: string;
  tipo: string;
  descrizione: string;
  data_movimento: string;
  user_id: string | null;
}

export interface TesoreriaSaldo {
  stagione_id: string;
  saldo_euro: string;
  num_movimenti: number;
  ultimi_movimenti: MovimentoTesoreria[];
}

export interface RosaPlayer {
  iscrizione_id: string;
  giocatore_id: string;
  nome: string;
  club_serie_a: string | null;
  ruolo_classic: string | null;
  ruoli_mantra: string[] | null;
  stato_giocatore: string;
  tipo_iscrizione: "provvisoria" | "ufficiale";
  da_stagione_precedente: boolean;
  /** Tipo di possesso del giocatore. Andrea 2026-05-19: serve al wizard
   * mercato per filtrare i cedibili (solo `proprieta`). */
  tipo_possesso?:
    | "proprieta"
    | "prestito_secco"
    | "prestito_ddr"
    | "prestito_odr"
    | "prestito_odr_condizionato"
    | null;
  /** Andrea 2026-09-07: età (tag U23) e valore TM corrente, mostrati
   * in Rose e Gestione Rosa. */
  eta?: number | null;
  valore_tm_milioni?: string | null;
  /** Andrea 2026-09-24: quando e' arrivato, quanto e' costato (asta o
   * mercato; uno scambio non ha prezzo) e quanto valeva su TM quel giorno. */
  data_acquisizione?: string | null;
  prezzo_acquisto_milioni?: string | null;
  origine_acquisto?: string | null;
  valore_tm_iniziale_milioni?: string | null;
}

export interface RosaResponse {
  squadra_id: string;
  stagione_id: string;
  totale: number;
  ufficiali: number;
  provvisorie: number;
  portieri_mantra: number;
  items: RosaPlayer[];
  // FASE 7 iter 2 — privacy
  privacy_visibility?: "visibile" | "aggregato" | "nascosto";
}

export interface Rinnovo {
  id: string;
  giocatore_id: string;
  valore_tm_riferimento_milioni: string;
  costo_rinnovo_milioni: number;
  esente: boolean;
  decisione_presidente: "rinnova" | "non_rinnova" | null;
  data_rinnovo: string | null;
}

export function useBudgetSaldo(stagioneId: string | null, squadraId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["budget", stagioneId, squadraId],
    queryFn: async () => {
      const res = await api.get<BudgetSaldo>(`/budget/${stagioneId}/${squadraId}`);
      return res.data;
    },
    enabled: isAuth && !!stagioneId && !!squadraId,
  });
}

interface BudgetTotale {
  squadra_id: string;
  saldo_milioni: string | null; // Decimal serialized as string
  num_movimenti: number;
  privacy_visibility?: string;
}

/** Saldo budget lifetime/cumulativo (cross-stagione) per la squadra. */
export function useBudgetTotale(squadraId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["budget-totale", squadraId],
    queryFn: async () => {
      const res = await api.get<BudgetTotale>(`/budget/squadra/${squadraId}/totale`);
      return res.data;
    },
    enabled: isAuth && !!squadraId,
  });
}

export function useTesoreriaSaldo(stagioneId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["tesoreria", stagioneId],
    queryFn: async () => {
      const res = await api.get<TesoreriaSaldo>(`/tesoreria/${stagioneId}`);
      return res.data;
    },
    enabled: isAuth && !!stagioneId,
  });
}

export function useRosa(stagioneId: string | null, squadraId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["rosa", stagioneId, squadraId],
    queryFn: async () => {
      const res = await api.get<RosaResponse>(`/rose/${stagioneId}/${squadraId}`);
      return res.data;
    },
    enabled: isAuth && !!stagioneId && !!squadraId,
  });
}

export function useRinnovi(stagioneId: string | null, squadraId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["rinnovi", stagioneId, squadraId],
    queryFn: async () => {
      const res = await api.get<Rinnovo[]>(`/rinnovi/${stagioneId}/${squadraId}`);
      return res.data;
    },
    enabled: isAuth && !!stagioneId && !!squadraId,
  });
}

// ============================================================
// Capitano/Vice stagionali (Andrea 2026-05-18 + Bridge v1.4 Sez. 1.9)
// ============================================================

export interface CapitanoStagione {
  squadra_id: string;
  stagione_id: string;
  // Pool dei 5 papabili capitani (Andrea 2026-06-11). [] se non impostato.
  pool_giocatori_ids: string[];
  pool_congelato: boolean;
  // Deprecati (cap/vice stagionali) — tenuti per compat.
  capitano_giocatore_id: string | null;
  vicecapitano_giocatore_id: string | null;
}

export interface PoolCapitaniPayload {
  pool_giocatori_ids: string[];
}

/** Pool papabili capitani della squadra. Ritorna pool [] + flag congelato. */
export function useCapitanoStagione(stagioneId: string | null, squadraId: string | null) {
  const isAuth = useAuthStore((s) => !!s.accessToken);
  return useQuery({
    queryKey: ["capitano-stagione", stagioneId, squadraId],
    queryFn: async () => {
      const res = await api.get<CapitanoStagione | null>(
        `/rose/${stagioneId}/${squadraId}/capitani`,
      );
      return res.data;
    },
    enabled: isAuth && !!stagioneId && !!squadraId,
  });
}

/** Imposta il pool di 5 papabili capitani. Solo presidente squadra (o admin).
 * Vincoli backend: esattamente 5 in lista 25 ufficiale, mercato aperto. */
export function useSetPoolCapitani() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      stagioneId: string;
      squadraId: string;
      payload: PoolCapitaniPayload;
    }) => {
      const res = await api.put<CapitanoStagione>(
        `/rose/${args.stagioneId}/${args.squadraId}/capitani`,
        args.payload,
      );
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["capitano-stagione", vars.stagioneId, vars.squadraId],
      });
    },
  });
}

// ============================================================
// Lista 25 — add/remove (Andrea 2026-05-18 + Bridge v1.4 Sez. 1.11)
// ============================================================

/** Aggiunge un giocatore alla lista 25 ufficiale. Solo a mercato aperto. */
export function useAggiungiAllaLista25() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { stagioneId: string; squadraId: string; giocatoreId: string }) => {
      const res = await api.post(`/rose/${args.stagioneId}/${args.squadraId}/lista-25`, {
        giocatore_id: args.giocatoreId,
      });
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["rosa", vars.stagioneId, vars.squadraId],
      });
    },
  });
}

/** Rimuove un giocatore dalla lista 25 (downgrade a provvisoria, append-only). */
export function useRimuoviDaLista25() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { iscrizioneId: string; stagioneId: string; squadraId: string }) => {
      await api.delete(`/rose/lista-25/${args.iscrizioneId}`);
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["rosa", vars.stagioneId, vars.squadraId],
      });
    },
  });
}

// ============================================================
// Ufficializza 25 / Swap rosa (Andrea 2026-05-18)
// ============================================================

export interface UfficializzaResponse {
  stagione_id: string;
  finestra_id: string;
  rose_finalizzate_at: string;
  messaggio: string;
}

/** Admin: chiude il mercato per la stagione (setta rose_finalizzate_at sulla
 * finestra attiva). Da quel momento solo swap rosa, non più convoca/escludi. */
export function useUfficializzaLista25() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { stagioneId: string }) => {
      const res = await api.post<UfficializzaResponse>(
        `/rose/admin/stagione/${args.stagioneId}/ufficializza-25`,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rosa"] });
      // Andrea 2026-05-21: invalida anche le finestre — il toggle
      // Lista APERTA/CHIUSA deriva da `rose_finalizzate_at` su quella
      // query; senza invalidazione la UI restava ferma fino al refresh.
      qc.invalidateQueries({ queryKey: ["stagione-finestre"] });
    },
  });
}

/** Admin: annulla l'ufficializzazione della lista 25 (rimette
 * rose_finalizzate_at=NULL sull'ultima finestra finalizzata). Riapre
 * convoca/escludi. Andrea 2026-05-19: serve per recuperare da
 * ufficializzazione fatta per errore. */
export function useRiapriLista25() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { stagioneId: string }) => {
      const res = await api.post<{
        stagione_id: string;
        finestra_id: string;
        messaggio: string;
      }>(`/rose/admin/stagione/${args.stagioneId}/riapri-lista-25`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["rosa"] });
      qc.invalidateQueries({ queryKey: ["stagione-finestre"] });
    },
  });
}

export interface SwapRosaResponse {
  swap_id: string;
  squadra_id: string;
  stagione_id: string;
  finestra_mercato_riferimento_id: string;
  giocatore_uscente_id: string;
  giocatore_entrante_id: string;
}

/** Swap rosa fuori sessione mercato: 1 per finestra chiusa.
 * Vincoli: mercato chiuso (rose_finalizzate_at valorizzato), uscente in
 * lista 25, entrante in possesso attivo non gia' in lista 25. */
export function useSwapRosa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      stagioneId: string;
      squadraId: string;
      uscenteId: string;
      entranteId: string;
    }) => {
      const res = await api.post<SwapRosaResponse>(
        `/rose/${args.stagioneId}/${args.squadraId}/swap`,
        {
          giocatore_uscente_id: args.uscenteId,
          giocatore_entrante_id: args.entranteId,
        },
      );
      return res.data;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({
        queryKey: ["rosa", vars.stagioneId, vars.squadraId],
      });
    },
  });
}
